/**
 * Discount Removal Extension for Update Meals
 * 
 * Handles discount removal modal and related functionality.
 */
(function() {
  'use strict';

  if (!window.etUpdateMealsExtensions) {
    window.etUpdateMealsExtensions = {};
  }

  const extensions = window.etUpdateMealsExtensions;
  let releaseFocusTrap = null;
  let previousFocusedElement = null;

  function escapeHtml(value) {
    const sharedEscape = window.etRechargeUtils?.escapeHtml;
    if (typeof sharedEscape === 'function') {
      return sharedEscape(value);
    }
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  function getOptionalRechargeMethod(pathSegments) {
    const sharedGetter = window.etRechargeUtils?.getOptionalRechargeMethod;
    if (typeof sharedGetter === 'function') {
      return sharedGetter(pathSegments);
    }
    try {
      const sharedSdk = window.etRechargeSdk;
      if (!sharedSdk || typeof sharedSdk.getMethod !== 'function') {
        return null;
      }
      return sharedSdk.getMethod(pathSegments);
    } catch (error) {
      return null;
    }
  }

  function removeDiscountsFromAddress(...args) {
    const path = ['address', 'removeDiscountsFromAddress'];
    const method = getOptionalRechargeMethod(path);
    if (!method) {
      throw new Error(
        'Recharge method "address.removeDiscountsFromAddress" is unavailable.'
      );
    }
    // Invalidates the shared read memo before the caller re-renders (#341).
    const sharedWrite = window.etRechargeUtils?.runRechargeWrite;
    return typeof sharedWrite === 'function' ? sharedWrite(path, ...args) : method(...args);
  }

  function openRemoveDiscountModal() {
    const state = extensions.getState?.();
    const resolveChargeDiscountMeta = extensions.resolveChargeDiscountMeta;
    const getPortalSession = extensions.getPortalSession;
    const showInlineErrorToast = extensions.showInlineErrorToast;

    if (!state || !resolveChargeDiscountMeta || !getPortalSession || !showInlineErrorToast) {
      console.warn('[Update Meals Discount] Extension dependencies not available');
      return;
    }

    const existing = document.querySelector(
      '.et-update-meals-discount-modal-overlay'
    );
    const activeCharge = state.overviewSlotCharge;
    const discountMeta = resolveChargeDiscountMeta(activeCharge || {});
    const code = discountMeta.primaryCode || '';

    if (existing) {
      const codeEl = existing.querySelector(
        '[data-role="discount-code-label"]'
      );
      if (codeEl) codeEl.textContent = code;
      
      const confirmButton = existing.querySelector('[data-role="confirm-remove-discount"]');
      if (confirmButton instanceof HTMLButtonElement) {
        confirmButton.disabled = false;
        confirmButton.textContent = 'Yes, remove reward';
      }
      
      existing.style.display = 'flex';
      previousFocusedElement =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      const dialog = existing.querySelector('.et-update-meals-discount-modal');
      const trapFocus = window.etRechargeUtils?.trapFocus;
      if (typeof trapFocus === 'function' && dialog instanceof HTMLElement) {
        if (typeof releaseFocusTrap === 'function') {
          releaseFocusTrap();
        }
        releaseFocusTrap = trapFocus(dialog, {
          onEscape: closeRemoveDiscountModal,
        });
      }
      return;
    }

    const overlay = document.createElement('div');
    overlay.className = 'et-update-meals-discount-modal-overlay';
    overlay.innerHTML = `
      <div class="et-update-meals-discount-modal" role="dialog" aria-modal="true" aria-label="Remove reward">
        <button type="button" class="et-update-meals-discount-modal__close" data-role="close-discount-modal" aria-label="Close">&times;</button>
        <h2 class="et-update-meals-discount-modal__title">Are you sure you want to remove this reward?</h2>
        <p class="et-update-meals-discount-modal__body">
          This will remove the reward for all orders with this address.
        </p>
        <p class="et-update-meals-discount-modal__code" data-role="discount-code-label">${escapeHtml(
          code
        )}</p>
        <div class="et-update-meals-discount-modal__actions">
          <button type="button" class="et-update-meals-discount-modal__button et-update-meals-discount-modal__button--primary" data-role="confirm-remove-discount">
            Yes, remove reward
          </button>
          <button type="button" class="et-update-meals-discount-modal__button et-update-meals-discount-modal__button--secondary" data-role="cancel-remove-discount">
            Cancel, keep reward
          </button>
        </div>
      </div>
    `;

    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) {
        closeRemoveDiscountModal();
      }
    });

    const closeButton = overlay.querySelector('[data-role="close-discount-modal"]');
    if (closeButton) {
      closeButton.addEventListener('click', closeRemoveDiscountModal);
    }

    const cancelButton = overlay.querySelector('[data-role="cancel-remove-discount"]');
    if (cancelButton) {
      cancelButton.addEventListener('click', closeRemoveDiscountModal);
    }

    const confirmButton = overlay.querySelector('[data-role="confirm-remove-discount"]');
    if (confirmButton) {
      confirmButton.addEventListener('click', handleConfirmRemoveDiscount);
    }

    document.body.appendChild(overlay);
    previousFocusedElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = overlay.querySelector('.et-update-meals-discount-modal');
    const trapFocus = window.etRechargeUtils?.trapFocus;
    if (typeof trapFocus === 'function' && dialog instanceof HTMLElement) {
      releaseFocusTrap = trapFocus(dialog, {
        onEscape: closeRemoveDiscountModal,
      });
    }
  }

  function closeRemoveDiscountModal() {
    const overlay = document.querySelector(
      '.et-update-meals-discount-modal-overlay'
    );
    if (overlay) {
      overlay.style.display = 'none';
    }
    if (typeof releaseFocusTrap === 'function') {
      releaseFocusTrap();
      releaseFocusTrap = null;
    }
    if (
      previousFocusedElement instanceof HTMLElement &&
      document.contains(previousFocusedElement)
    ) {
      previousFocusedElement.focus();
      previousFocusedElement = null;
    }
  }

  async function handleConfirmRemoveDiscount() {
    const state = extensions.getState?.();
    const getPortalSession = extensions.getPortalSession;
    const showInlineErrorToast = extensions.showInlineErrorToast;
    const clearOverviewDiscountOptimistic =
      extensions.clearOverviewDiscountOptimistic;

    if (!state || !getPortalSession || !showInlineErrorToast) {
      console.warn('[Update Meals Discount] Extension dependencies not available');
      return;
    }

    const addressId = state.overviewSlotAddressId;
    if (!addressId) {
      console.warn(
        '[Update Meals] No address context available for discount removal.'
      );
      closeRemoveDiscountModal();
      return;
    }

    const overlay = document.querySelector(
      '.et-update-meals-discount-modal-overlay'
    );
    const primaryButton =
      overlay &&
      overlay.querySelector('[data-role="confirm-remove-discount"]');
    if (primaryButton instanceof HTMLButtonElement) {
      primaryButton.disabled = true;
    }

    try {
      const session = await getPortalSession();
      await removeDiscountsFromAddress(session, addressId);
      if (typeof clearOverviewDiscountOptimistic === 'function') {
        clearOverviewDiscountOptimistic();
      }
      closeRemoveDiscountModal();

      const analytics = {
        pathname: window.location.pathname || '',
        timestamp: new Date().toISOString(),
      };

      document.dispatchEvent(new CustomEvent('Affinity:refresh'));
      document.dispatchEvent(
        new CustomEvent('Recharge::action::orderChanged', {
          detail: { analytics },
        })
      );
      document.dispatchEvent(
        new CustomEvent('Recharge::action::reschedule', {
          detail: { analytics },
        })
      );
    } catch (error) {
      console.warn(
        '[Update Meals] Failed to remove discount from charge.',
        error
      );
      showInlineErrorToast(
        'Unable to remove discount right now. Please try again.'
      );
      if (primaryButton instanceof HTMLButtonElement) {
        primaryButton.disabled = false;
      }
    }
  }

  extensions.discount = {
    openRemoveDiscountModal,
    closeRemoveDiscountModal,
    handleConfirmRemoveDiscount,
  };

})();
